import React, { useState, useContext, useEffect } from "react";
import { AuthContext, API, APP_NAME } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, Eye, EyeOff } from "lucide-react";

const SettingsPage = () => {
  const { shop, token } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    shop_name: "",
    address: "",
    contact: "",
    service_types: [],
    shop_notes: "",
  });
  const [serviceTypeInput, setServiceTypeInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [logoPreview, setLogoPreview] = useState(null);
  const [activeTab, setActiveTab] = useState("settings");
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [connectingCalendar, setConnectingCalendar] = useState(false);
  const [calendars, setCalendars] = useState([]);
  const [disconnectingCalendar, setDisconnectingCalendar] = useState(false);

  // Password change state
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    if (shop) {
      setFormData({
        shop_name: shop.shop_name || "",
        address: shop.address || "",
        contact: shop.contact || "",
        // appointment_types: shop.appointment_types || [],
        service_types: shop.service_types || [],
        shop_notes: shop.shop_notes || "",
      });
      setLogoPreview(shop.logo_url);
    }
  }, [shop]);

  // Update page title and fetch service types
  useEffect(() => {
    document.title = `BackOfficeSettings - ${APP_NAME}`;

    const fetchServiceTypes = async () => {
      try {
        const response = await axios.get(`${API}/service-types`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setServiceTypes(response.data);
      } catch (error) {
        console.error("Error fetching service types:", error);
      }
    };

    fetchServiceTypes();
  }, [token]);

  // Fetch calendars to check connection status
  useEffect(() => {
    const fetchCalendars = async () => {
      try {
        const response = await axios.get(`${API}/shop/calendars`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCalendars(response.data);
        setCalendarConnected(response.data.length > 0);
      } catch (error) {
        console.error("Error fetching calendars:", error);
        setCalendars([]);
        setCalendarConnected(false);
      }
    };

    if (token) {
      fetchCalendars();
    }
  }, [token]);

  // Set initial service type selection after both shop and serviceTypes are loaded
  useEffect(() => {
    if (shop?.service_types?.length && serviceTypes.length && !serviceTypeInput) {
      const initialServiceTypeId = shop.service_types[0]._id;
      // Verify the ID exists in the serviceTypes options
      const exists = serviceTypes.some(type => type._id === initialServiceTypeId);
      if (exists) {
        setServiceTypeInput(initialServiceTypeId);
      }
    }
  }, [shop, serviceTypes, serviceTypeInput]);

  const handleServiceTypeChange = (typeId) => {
    const selectedType = serviceTypes.find(type => type._id === typeId);
    setServiceTypeInput(typeId);
    setFormData((prevFormData) => ({ ...prevFormData, service_types: [selectedType] }));
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Update profile
      await axios.put(`${API}/shop/profile`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Upload logo if selected
      if (logoFile) {
        const formDataFile = new FormData();
        formDataFile.append("file", logoFile);
        await axios.post(`${API}/shop/logo`, formDataFile, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });
      }

      toast.success("Settings updated successfully!");
      // window.location.reload();
    } catch (error) {
      toast.error("Failed to update settings");
    } finally {
      setLoading(false);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Google Calendar Integration
  const handleConnectCalendar = () => {
    setConnectingCalendar(true);

    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

    if (!clientId) {
      toast.error("Google Client ID not configured");
      setConnectingCalendar(false);
      return;
    }

    // Initialize Google OAuth
    const client = window.google.accounts.oauth2.initCodeClient({
      client_id: clientId,
      access_type: 'offline',
      prompt: 'consent',
      ux_mode: 'popup',
      scope: 'https://www.googleapis.com/auth/calendar https://www.googleapis.com/auth/calendar.events',
      callback: async (response) => {
        if (response.error) {
          toast.error("Failed to connect to Google Calendar");
          setConnectingCalendar(false);
          return;
        }

        try {
          // Send the access token to backend
          await axios.post(
            `${API}/shop/calendar/connect`,
            { code: response.code, type: 'google' },
            { headers: { Authorization: `Bearer ${token}` } }
          );

          // Refetch calendars to update connection status
          const calendarsResponse = await axios.get(`${API}/shop/calendars`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setCalendars(calendarsResponse.data);
          setCalendarConnected(calendarsResponse.data.length > 0);

          toast.success("Google Calendar connected successfully!");
        } catch (error) {
          console.error("Error saving calendar token:", error);
          toast.error("Failed to save calendar connection");
        } finally {
          setConnectingCalendar(false);
        }
      },
    });

    // Request access token
    client.requestCode();
  };

  // Disconnect Google Calendar
  const handleDisconnectCalendar = async () => {
    if (calendars.length === 0) {
      toast.error("No calendar to disconnect");
      return;
    }

    setDisconnectingCalendar(true);

    try {
      await axios.post(
        `${API}/shop/calendar/disconnect`,
        { calendar_id: calendars[0]._id },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Refetch calendars to update connection status
      const calendarsResponse = await axios.get(`${API}/shop/calendars`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCalendars(calendarsResponse.data);
      setCalendarConnected(calendarsResponse.data.length > 0);

      toast.success("Google Calendar disconnected successfully!");
    } catch (error) {
      console.error("Error disconnecting calendar:", error);
      toast.error("Failed to disconnect calendar");
    } finally {
      setDisconnectingCalendar(false);
    }
  };

  // Handle Password Change
  const handlePasswordChange = async (e) => {
    e.preventDefault();

    // Validation
    if (!passwordData.current_password || !passwordData.new_password || !passwordData.confirm_password) {
      toast.error("All fields are required");
      return;
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error("New passwords do not match");
      return;
    }

    if (passwordData.new_password === passwordData.current_password) {
      toast.error("New password must be different from current password");
      return;
    }

    if (passwordData.new_password.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }

    setChangingPassword(true);

    try {
      await axios.put(
        `${API}/auth/password`,
        {
          current_password: passwordData.current_password,
          new_password: passwordData.new_password
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Password changed successfully!");

      // Clear form
      setPasswordData({
        current_password: "",
        new_password: "",
        confirm_password: ""
      });
      setShowPasswords({
        current: false,
        new: false,
        confirm: false
      });
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="max-w-3xl">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-[#f8f8f8] mb-2">BackOffice Settings</h1>
        <p className="text-lg text-[#f8f8f8]/70">Customize your profile</p>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6">
        <Button
          type="button"
          onClick={() => setActiveTab("settings")}
          variant={activeTab === "settings" ? "default" : "outline"}
          className={
            activeTab === "settings"
              ? "bg-[#00ced1] hover:bg-[#00ced1]/90 text-[#0a1a1f] font-semibold"
              : "border-[#00ced1] text-[#00ced1] hover:bg-[#00ced1]/10 hover:text-[#00ced1]"
          }
        >
          Settings
        </Button>
        <Button
          type="button"
          onClick={() => setActiveTab("calendar")}
          variant={activeTab === "calendar" ? "default" : "outline"}
          className={
            activeTab === "calendar"
              ? "bg-[#00ced1] hover:bg-[#00ced1]/90 text-[#0a1a1f] font-semibold"
              : "border-[#00ced1] text-[#00ced1] hover:bg-[#00ced1]/10 hover:text-[#00ced1]"
          }
        >
          Calendar
        </Button>
        <Button
          type="button"
          onClick={() => setActiveTab("password")}
          variant={activeTab === "password" ? "default" : "outline"}
          className={
            activeTab === "password"
              ? "bg-[#00ced1] hover:bg-[#00ced1]/90 text-[#0a1a1f] font-semibold"
              : "border-[#00ced1] text-[#00ced1] hover:bg-[#00ced1]/10 hover:text-[#00ced1]"
          }
        >
          Change Password
        </Button>
      </div>

      {/* Settings Tab Content */}
      {activeTab === "settings" && (
        <form onSubmit={handleUpdate} className="space-y-6">
          <div className="bg-[#005f6b]/20 backdrop-blur-sm rounded-2xl border border-[#d4af37]/30 p-8">
            {/* Logo Upload */}
            <div className="mb-6">
              <Label className="text-[#f8f8f8] mb-2 block">BackOffice Logo</Label>
              <div className="flex items-center gap-6">
                {logoPreview && (
                  <div className="w-24 h-24 rounded-lg overflow-hidden border border-[#d4af37]/30">
                    <img
                      src={logoPreview}
                      alt="Logo preview"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div>
                  <input
                    type="file"
                    id="logo-upload"
                    data-testid="logo-upload-input"
                    accept="image/*"
                    onChange={handleLogoChange}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    data-testid="logo-upload-btn"
                    onClick={() => document.getElementById("logo-upload").click()}
                    variant="outline"
                    className="border-[#00ced1] text-[#00ced1] hover:bg-[#00ced1]/10"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Logo
                  </Button>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="shop_name" className="text-[#f8f8f8]">Business Name</Label>
                <Input
                  id="shop_name"
                  data-testid="settings-shop-name-input"
                  value={formData.shop_name}
                  onChange={(e) => setFormData({ ...formData, shop_name: e.target.value })}
                  className="mt-2 bg-[#0a1a1f]/60 border-[#005f6b] text-[#f8f8f8] placeholder:text-[#f8f8f8]/40 focus:border-[#00ced1] focus:ring-[#00ced1]"
                />
              </div>

              <div>
                <Label htmlFor="address" className="text-[#f8f8f8]">Address</Label>
                <Input
                  id="address"
                  data-testid="settings-address-input"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="123 Main St, City, State"
                  className="mt-2 bg-[#0a1a1f]/60 border-[#005f6b] text-[#f8f8f8] placeholder:text-[#f8f8f8]/40 focus:border-[#00ced1] focus:ring-[#00ced1]"
                />
              </div>

              <div>
                <Label htmlFor="contact" className="text-[#f8f8f8]">Contact</Label>
                <Input
                  id="contact"
                  data-testid="settings-contact-input"
                  value={formData.contact}
                  onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                  placeholder="(555) 123-4567 or email@example.com"
                  className="mt-2 bg-[#0a1a1f]/60 border-[#005f6b] text-[#f8f8f8] placeholder:text-[#f8f8f8]/40 focus:border-[#00ced1] focus:ring-[#00ced1]"
                />
              </div>

              <div>
                <Label className="text-[#f8f8f8] mb-2 block">Service Type</Label>
                <div className="flex gap-2 mb-3">
                  <Select value={serviceTypeInput} onValueChange={handleServiceTypeChange}>
                    <SelectTrigger className="bg-[#0a1a1f]/60 border-[#005f6b] text-[#f8f8f8]">
                      <SelectValue placeholder="Select service type" />
                    </SelectTrigger>
                    <SelectContent>
                      {serviceTypes.map((type) => (
                        <SelectItem key={type._id} value={type._id}>
                          {type.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="shop_notes" className="text-[#f8f8f8]">Shop Notes</Label>
                <Textarea
                  id="shop_notes"
                  data-testid="settings-shop-notes-input"
                  value={formData.shop_notes}
                  onChange={(e) => setFormData({ ...formData, shop_notes: e.target.value })}
                  placeholder="e.g., We tend to run small in jackets. We specialize in modern cuts."
                  rows={4}
                  className="mt-2 bg-[#0a1a1f]/60 border-[#005f6b] text-[#f8f8f8] placeholder:text-[#f8f8f8]/40 focus:border-[#00ced1] focus:ring-[#00ced1]"
                />
              </div>
            </div>

            <Button
              data-testid="save-settings-btn"
              type="submit"
              disabled={loading}
              className="w-full mt-6 bg-[#00ced1] hover:bg-[#00ced1]/90 text-[#0a1a1f] font-semibold py-6 rounded-xl"
            >
              {loading ? "Saving..." : "Save Settings"}
            </Button>
          </div>
        </form>
      )}

      {/* Calendar Tab Content */}
      {activeTab === "calendar" && (
        <div className="bg-[#005f6b]/20 backdrop-blur-sm rounded-2xl border border-[#d4af37]/30 p-8">
          <p className="text-[#f8f8f8]/70 text-center">
            {calendarConnected ? (
              <span>Your Google Calendar is connected to BackOffice.</span>
            ) : (
              <span>Connect your Google Calendar to BackOffice to sync your appointments.</span>
            )}
          </p>
          <div className="text-center">
            <Button
              data-testid="connect-calendar-btn"
              type="button"
              onClick={calendarConnected ? handleDisconnectCalendar : handleConnectCalendar}
              disabled={connectingCalendar || disconnectingCalendar}
              className="mt-6 bg-[#00ced1] hover:bg-[#00ced1]/90 text-[#0a1a1f] font-semibold py-6 rounded-xl"
            >
              {connectingCalendar
                ? "Connecting..."
                : disconnectingCalendar
                  ? "Disconnecting..."
                  : calendarConnected
                    ? "Disconnect"
                    : "Connect"}
            </Button>
          </div>
        </div>
      )}

      {/* Password Tab Content */}
      {activeTab === "password" && (
        <form onSubmit={handlePasswordChange} className="space-y-6">
          <div className="bg-[#005f6b]/20 backdrop-blur-sm rounded-2xl border border-[#d4af37]/30 p-8">
            <div className="space-y-4">
              {/* Current Password */}
              <div>
                <Label htmlFor="current_password" className="text-[#f8f8f8]">Current Password</Label>
                <div className="relative mt-2">
                  <Input
                    id="current_password"
                    data-testid="current-password-input"
                    type={showPasswords.current ? "text" : "password"}
                    value={passwordData.current_password}
                    onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                    className="bg-[#0a1a1f]/60 border-[#005f6b] text-[#f8f8f8] placeholder:text-[#f8f8f8]/40 focus:border-[#00ced1] focus:outline-none focus:ring-0 pr-10"
                    placeholder="Enter current password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#f8f8f8]/60 hover:text-[#00ced1] transition-colors"
                  >
                    {showPasswords.current ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* New Password */}
              <div>
                <Label htmlFor="new_password" className="text-[#f8f8f8]">New Password</Label>
                <div className="relative mt-2">
                  <Input
                    id="new_password"
                    data-testid="new-password-input"
                    type={showPasswords.new ? "text" : "password"}
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                    className="bg-[#0a1a1f]/60 border-[#005f6b] text-[#f8f8f8] placeholder:text-[#f8f8f8]/40 focus:border-[#00ced1] focus:outline-none focus:ring-0 pr-10"
                    placeholder="Enter new password (min 6 characters)"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#f8f8f8]/60 hover:text-[#00ced1] transition-colors"
                  >
                    {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <Label htmlFor="confirm_password" className="text-[#f8f8f8]">Confirm New Password</Label>
                <div className="relative mt-2">
                  <Input
                    id="confirm_password"
                    data-testid="confirm-password-input"
                    type={showPasswords.confirm ? "text" : "password"}
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                    className="bg-[#0a1a1f]/60 border-[#005f6b] text-[#f8f8f8] placeholder:text-[#f8f8f8]/40 focus:border-[#00ced1] focus:outline-none focus:ring-0 pr-10"
                    placeholder="Confirm new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#f8f8f8]/60 hover:text-[#00ced1] transition-colors"
                  >
                    {showPasswords.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            </div>

            <Button
              data-testid="change-password-btn"
              type="submit"
              disabled={changingPassword}
              className="w-full mt-6 bg-[#00ced1] hover:bg-[#00ced1]/90 text-[#0a1a1f] font-semibold py-6 rounded-xl"
            >
              {changingPassword ? "Changing Password..." : "Change Password"}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};

export default SettingsPage;
